/**
 * Riport-export segédmodul (PDF + CSV).
 *
 * - logo lazy-load + cache (fetch a /pta-logo-XXX.png-ből, base64 DataURL).
 * - jsPDF + autotable lazy import.
 * - Közös fejléc (logo, cím, csoport+intézmény+tanév, időpont, szűrők).
 * - Közös lábléc (rendszer + készítő user + oldalszám).
 * - Logo-alapú vízjel (850px, 0.12 opacity, 45° dőlt) minden oldalon.
 * - CSV export választható elválasztóval (`;` / `,` / `\t`).
 *
 * Pénzformázás: a response-ból olvasott `numberFormat` + `currencySymbol`
 * + `currencyMinorUnit` mezőket használja (a riport mindig az adott csoport
 * lokalizációja szerint formáz, függetlenül a FE aktív csoportjától).
 */

import type {
  PaymentMethod,
  ReportByMethodResponse,
  ReportByRequirementResponse,
  ReportByStudentResponse,
  ReportFilters,
  ReportResponseBase,
  ReportRowStatus,
} from '@pta/types';
import { formatMoney } from '@pta/i18n/format';

export const PM_LABEL: Record<PaymentMethod, string> = {
  cash: 'Készpénz',
  bank_transfer: 'Banki átutalás',
  card: 'Kártya',
  escrow: 'Letét',
};

export const STATUS_LABEL: Record<ReportRowStatus, string> = {
  paid: 'Fizetett',
  partial: 'Részleges',
  unpaid: 'Nincs fizetve',
  waived: 'Mentesítve',
};

export const STATUS_COLOR: Record<ReportRowStatus, string> = {
  paid: 'teal',
  partial: 'yellow',
  unpaid: 'red',
  waived: 'gray',
};

/** Riport response-ból pénzformázó closure. */
function makeMoney(resp: ReportResponseBase) {
  return (value: string | number) =>
    formatMoney(value, resp.numberFormat, resp.currencySymbol, resp.currencyMinorUnit);
}

// ============================================================
// CSV elválasztó
// ============================================================

export type CsvSeparator = ';' | ',' | '\t';

export const CSV_SEPARATOR_OPTIONS: Array<{ value: CsvSeparator; label: string }> = [
  { value: ';', label: 'Pontosvessző (;)  — Excel default' },
  { value: ',', label: 'Vessző (,)' },
  { value: '\t', label: 'Tabulátor' },
];

function csvCell(s: string | number, sep: CsvSeparator): string {
  const v = String(s);
  const needsQuoting = v.includes(sep) || v.includes('"') || v.includes('\n') || v.includes('\r');
  if (needsQuoting) {
    return '"' + v.replace(/"/g, '""') + '"';
  }
  return v;
}

export function csvRow(cells: Array<string | number>, sep: CsvSeparator): string {
  return cells.map((c) => csvCell(c, sep)).join(sep);
}

export function downloadCsv(csv: string, filename: string) {
  const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, filename);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ============================================================
// CSV builder-ek (3 view-hez)
// ============================================================

export function byStudentCsv(data: ReportByStudentResponse, sep: CsvSeparator): string {
  const money = makeMoney(data);
  const meta = csvMetaHeader(data, sep);
  const header = csvRow(
    ['Diák', 'Identity', 'Várt', 'Befizetve', 'Hátralék', 'Targets', 'Mentesítve', 'Státusz'],
    sep,
  );
  const rows = data.rows.map((r) =>
    csvRow(
      [
        `${r.lastName} ${r.firstName}`,
        r.identityStatus,
        money(r.expectedTotal),
        money(r.paidTotal),
        money(r.remaining),
        r.targetCount,
        r.waivedCount,
        STATUS_LABEL[r.status],
      ],
      sep,
    ),
  );
  const totals = csvRow(
    [
      `Összesen (${data.totals.studentCount} diák)`,
      '',
      money(data.totals.expectedTotal),
      money(data.totals.paidTotal),
      money(data.totals.remaining),
      '',
      '',
      '',
    ],
    sep,
  );
  return [...meta, header, ...rows, totals].join('\n');
}

export function byRequirementCsv(data: ReportByRequirementResponse, sep: CsvSeparator): string {
  const money = makeMoney(data);
  const meta = csvMetaHeader(data, sep);
  const header = csvRow(
    [
      'Előírás',
      'Cél',
      'Esedékes',
      'Várt',
      'Befizetve',
      'Hátralék',
      'Targets',
      'Fizetett',
      'Részleges',
      'Nincs',
      'Mentesítve',
      'Státusz',
    ],
    sep,
  );
  const rows = data.rows.map((r) =>
    csvRow(
      [
        r.title,
        r.purpose,
        r.dueDate ?? '',
        money(r.expectedTotal),
        money(r.paidTotal),
        money(r.remaining),
        r.targetCount,
        r.paidTargetCount,
        r.partialTargetCount,
        r.unpaidTargetCount,
        r.waivedTargetCount,
        STATUS_LABEL[r.rowStatus],
      ],
      sep,
    ),
  );
  const totals = csvRow(
    [
      `Összesen (${data.totals.requirementCount} előírás)`,
      '',
      '',
      money(data.totals.expectedTotal),
      money(data.totals.paidTotal),
      money(data.totals.remaining),
      '',
      '',
      '',
      '',
      '',
      '',
    ],
    sep,
  );
  return [...meta, header, ...rows, totals].join('\n');
}

export function byMethodCsv(data: ReportByMethodResponse, sep: CsvSeparator): string {
  const money = makeMoney(data);
  const meta = csvMetaHeader(data, sep);
  const header = csvRow(
    ['Fizetési mód', 'Befizetések', 'Összeg', 'Targets', 'Előírások'],
    sep,
  );
  const rows = data.rows.map((r) =>
    csvRow(
      [
        PM_LABEL[r.paymentMethod],
        r.paymentCount,
        money(r.paidTotal),
        r.targetCount,
        r.requirementCount,
      ],
      sep,
    ),
  );
  const totals = csvRow(
    ['Összesen', data.totals.paymentCount, money(data.totals.paidTotal), '', ''],
    sep,
  );
  return [...meta, header, ...rows, totals].join('\n');
}

function csvMetaHeader(data: ReportResponseBase, sep: CsvSeparator): string[] {
  const ts = new Date(data.generatedAt).toLocaleString('hu-HU');
  const filterStr = formatFilterSummary(data.filters);
  return [
    csvRow(['Csoport', data.groupName], sep),
    csvRow(['Intézmény', data.institutionLabel ?? ''], sep),
    csvRow(['Tanév', data.schoolYearDisplayName], sep),
    csvRow(['Generálva', ts], sep),
    csvRow(['Készítette', `${data.generatedBy.displayName} <${data.generatedBy.email}>`], sep),
    csvRow(['Pénznem', data.currency], sep),
    csvRow(['Szűrők', filterStr], sep),
    '',
  ];
}

function formatFilterSummary(filters: ReportFilters): string {
  const parts: string[] = [];
  if (filters.paidFrom || filters.paidTo) {
    parts.push(`Befizetés: ${filters.paidFrom ?? '...'}–${filters.paidTo ?? '...'}`);
  }
  if (filters.dueFrom || filters.dueTo) {
    parts.push(`Esedékes: ${filters.dueFrom ?? '...'}–${filters.dueTo ?? '...'}`);
  }
  if (filters.paymentMethod) parts.push(`Mód: ${PM_LABEL[filters.paymentMethod]}`);
  if (filters.status) parts.push(`Státusz: ${STATUS_LABEL[filters.status]}`);
  return parts.length === 0 ? '(nincs)' : parts.join(' • ');
}

// ============================================================
// Logo cache + lazy load
// ============================================================

interface LogoData {
  dataUrl: string;
  width: number;
  height: number;
}

const logoCache: Map<string, Promise<LogoData>> = new Map();

async function loadLogo(url: string): Promise<LogoData> {
  let cached = logoCache.get(url);
  if (!cached) {
    cached = (async () => {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Logo fetch failed: ${resp.status}`);
      const blob = await resp.blob();
      const dataUrl = await blobToDataUrl(blob);
      const dims = await imageDimensions(dataUrl);
      return { dataUrl, width: dims.width, height: dims.height };
    })();
    logoCache.set(url, cached);
  }
  return cached;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function imageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

// ============================================================
// jsPDF lazy import
// ============================================================

interface JsPdfApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  doc: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  autoTable: (doc: any, opts: Record<string, unknown>) => void;
}

async function loadJsPdf(): Promise<JsPdfApi> {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autoTable = (autoTableMod as any).default ?? autoTableMod;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  return { doc, autoTable };
}

// ============================================================
// PDF közös fej / lábléc / vízjel
// ============================================================

const HEADER_TITLE: Record<'by-student' | 'by-requirement' | 'by-method', string> = {
  'by-student': 'Diákonkénti riport',
  'by-requirement': 'Előírásonkénti riport',
  'by-method': 'Fizetési mód szerinti riport',
};

const PDF_SYSTEM_NAME = 'PTA — szmk-net.hu';
const HEAD_FILL: [number, number, number] = [38, 166, 154];
const FOOT_FILL: [number, number, number] = [220, 220, 220];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function decoratePages(doc: any, smallLogo: LogoData, bigLogo: LogoData, view: 'by-student' | 'by-requirement' | 'by-method', data: ReportResponseBase) {
  const totalPages = doc.internal.pages.length - 1;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawWatermark(doc, bigLogo, pageWidth, pageHeight);
    drawHeader(doc, smallLogo, view, data, pageWidth);
    drawFooter(doc, data, i, totalPages, pageWidth, pageHeight);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawWatermark(doc: any, bigLogo: LogoData, pageWidth: number, pageHeight: number) {
  const targetW = Math.min(pageWidth * 0.6, pageHeight * 0.6);
  const aspect = bigLogo.height / bigLogo.width;
  const targetH = targetW * aspect;
  const cx = pageWidth / 2;
  const cy = pageHeight / 2;

  if (typeof doc.GState === 'function' || typeof doc.setGState === 'function') {
    const gs = new (doc as { GState: new (o: { opacity: number }) => unknown }).GState({ opacity: 0.12 });
    doc.setGState(gs);
  }
  doc.addImage(
    bigLogo.dataUrl,
    'PNG',
    cx - targetW / 2,
    cy - targetH / 2,
    targetW,
    targetH,
    undefined,
    'FAST',
    -45,
  );
  if (typeof doc.GState === 'function' || typeof doc.setGState === 'function') {
    const gs = new (doc as { GState: new (o: { opacity: number }) => unknown }).GState({ opacity: 1 });
    doc.setGState(gs);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawHeader(doc: any, smallLogo: LogoData, view: 'by-student' | 'by-requirement' | 'by-method', data: ReportResponseBase, pageWidth: number) {
  const logoH = 14;
  const logoAspect = smallLogo.width / smallLogo.height;
  const logoW = logoH * logoAspect;
  doc.addImage(smallLogo.dataUrl, 'PNG', 10, 8, logoW, logoH);

  const titleX = 10 + logoW + 6;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(HEADER_TITLE[view], titleX, 14);

  doc.setFontSize(9);
  const inst = data.institutionLabel ?? '—';
  doc.text(`${data.groupName}  •  ${inst}  •  Tanév: ${data.schoolYearDisplayName}`, titleX, 20);

  const ts = new Date(data.generatedAt).toLocaleString('hu-HU');
  doc.text(`Generálva: ${ts}  •  Pénznem: ${data.currency}`, titleX, 26);

  const filterStr = formatFilterSummary(data.filters);
  if (filterStr !== '(nincs)') {
    doc.text(`Szűrők: ${filterStr}`, titleX, 32);
  }

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.line(10, 36, pageWidth - 10, 36);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function drawFooter(doc: any, data: ReportResponseBase, pageNum: number, totalPages: number, pageWidth: number, pageHeight: number) {
  const y = pageHeight - 8;
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.2);
  doc.line(10, y - 4, pageWidth - 10, y - 4);
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(PDF_SYSTEM_NAME, 10, y);
  const author = `Készítette: ${data.generatedBy.displayName} <${data.generatedBy.email}>`;
  doc.text(author, pageWidth / 2, y, { align: 'center' });
  doc.text(`${pageNum} / ${totalPages}`, pageWidth - 10, y, { align: 'right' });
}

const PDF_TABLE_START_Y = 42;

// ============================================================
// PDF builder-ek (3 view-hez)
// ============================================================

export async function byStudentPdf(data: ReportByStudentResponse, filename: string) {
  const money = makeMoney(data);
  const { doc, autoTable } = await loadJsPdf();
  const [smallLogo, bigLogo] = await Promise.all([
    loadLogo('/pta-logo-250.png'),
    loadLogo('/pta-logo-850.png'),
  ]);

  autoTable(doc, {
    startY: PDF_TABLE_START_Y,
    margin: { top: PDF_TABLE_START_Y, bottom: 14 },
    head: [['Diák', 'Várt', 'Befizetve', 'Hátralék', 'Targets', 'Státusz']],
    body: data.rows.map((r) => [
      `${r.lastName} ${r.firstName}`,
      money(r.expectedTotal),
      money(r.paidTotal),
      money(r.remaining),
      String(r.targetCount),
      STATUS_LABEL[r.status],
    ]),
    foot: [
      [
        `Összesen (${data.totals.studentCount} diák)`,
        money(data.totals.expectedTotal),
        money(data.totals.paidTotal),
        money(data.totals.remaining),
        '',
        '',
      ],
    ],
    styles: { fontSize: 9 },
    headStyles: { fillColor: HEAD_FILL },
    footStyles: { fillColor: FOOT_FILL, textColor: 0 },
  });

  decoratePages(doc, smallLogo, bigLogo, 'by-student', data);
  doc.save(filename);
}

export async function byRequirementPdf(data: ReportByRequirementResponse, filename: string) {
  const money = makeMoney(data);
  const { doc, autoTable } = await loadJsPdf();
  const [smallLogo, bigLogo] = await Promise.all([
    loadLogo('/pta-logo-250.png'),
    loadLogo('/pta-logo-850.png'),
  ]);

  autoTable(doc, {
    startY: PDF_TABLE_START_Y,
    margin: { top: PDF_TABLE_START_Y, bottom: 14 },
    head: [['Előírás', 'Esedékes', 'Várt', 'Befizetve', 'Hátralék', 'Targets', 'Státusz']],
    body: data.rows.map((r) => [
      r.title,
      r.dueDate ?? '—',
      money(r.expectedTotal),
      money(r.paidTotal),
      money(r.remaining),
      `${r.paidTargetCount}/${r.targetCount}`,
      STATUS_LABEL[r.rowStatus],
    ]),
    foot: [
      [
        `Összesen (${data.totals.requirementCount} előírás)`,
        '',
        money(data.totals.expectedTotal),
        money(data.totals.paidTotal),
        money(data.totals.remaining),
        '',
        '',
      ],
    ],
    styles: { fontSize: 9 },
    headStyles: { fillColor: HEAD_FILL },
    footStyles: { fillColor: FOOT_FILL, textColor: 0 },
  });

  decoratePages(doc, smallLogo, bigLogo, 'by-requirement', data);
  doc.save(filename);
}

export async function byMethodPdf(data: ReportByMethodResponse, filename: string) {
  const money = makeMoney(data);
  const { doc, autoTable } = await loadJsPdf();
  const [smallLogo, bigLogo] = await Promise.all([
    loadLogo('/pta-logo-250.png'),
    loadLogo('/pta-logo-850.png'),
  ]);

  autoTable(doc, {
    startY: PDF_TABLE_START_Y,
    margin: { top: PDF_TABLE_START_Y, bottom: 14 },
    head: [['Fizetési mód', 'Befizetések', 'Összeg', 'Targets', 'Előírások']],
    body: data.rows.map((r) => [
      PM_LABEL[r.paymentMethod],
      String(r.paymentCount),
      money(r.paidTotal),
      String(r.targetCount),
      String(r.requirementCount),
    ]),
    foot: [
      [
        'Összesen',
        String(data.totals.paymentCount),
        money(data.totals.paidTotal),
        '',
        '',
      ],
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: HEAD_FILL },
    footStyles: { fillColor: FOOT_FILL, textColor: 0 },
  });

  decoratePages(doc, smallLogo, bigLogo, 'by-method', data);
  doc.save(filename);
}
